import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { getDataSourceActivity } from './hubApi';
import { BRAND, OPERATION, INK, FONT_MONO } from 'Utils/styleTokens';

// Series palette from the mockup: discovery=blue, profile=cyan, masking=violet,
// encrypt/decrypt=encrypt-op color.
// Imported runs (Delphix profiling executions synced by an integration import) get their own
// series so their bars are distinguishable from runs C2 executed itself.
// Failures are NOT a series of their own — a failed job counts in its own family's bar, so this
// timeline reads as "what ran against this source" rather than as an error report.
const SERIES = [
  { key: 'DISCOVER', label: 'Discovery', color: BRAND.blue },
  { key: 'PROFILE', label: 'Fingerprint scans', color: BRAND.cyan },
  { key: 'MASKING', label: 'Masking', color: BRAND.violet },
  { key: 'ENCRYPT', label: 'Encrypt / Decrypt', color: OPERATION.encrypt.color },
  { key: 'IMPORTED', label: 'Imported profiles', color: BRAND.violet300 },
];

const RANGES = [
  { key: '13w', label: '13 weeks', weeks: 13, bucket: 'week' },
  { key: '6m', label: '6 months', months: 6, bucket: 'month' },
  { key: '12m', label: '12 months', months: 12, bucket: 'month' },
];

const seriesKeyOf = (event) => {
  if (event.imported === true) return 'IMPORTED';
  const jt = event.jobType || '';
  if (jt.includes('DISCOVER')) return 'DISCOVER';
  if (jt.includes('PROFILE')) return 'PROFILE';
  if (jt.includes('MASK')) return 'MASKING';
  if (jt.includes('ENCRYPT') || jt.includes('DECRYPT')) return 'ENCRYPT';
  return null;
};

const startOfWeek = (d) => {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - date.getDay());
  return date;
};

const buildBuckets = (range) => {
  const buckets = [];
  const now = new Date();
  if (range.bucket === 'week') {
    const start = startOfWeek(now);
    for (let i = range.weeks - 1; i >= 0; i -= 1) {
      const d = new Date(start);
      d.setDate(d.getDate() - i * 7);
      buckets.push({ start: d, label: `${d.getMonth() + 1}/${d.getDate()}` });
    }
  } else {
    for (let i = range.months - 1; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({ start: d, label: d.toLocaleString('en-US', { month: 'short' }) });
    }
  }
  return buckets;
};

const bucketIndexFor = (ts, buckets, range) => {
  const t = Date.parse(ts);
  if (!Number.isFinite(t)) return -1;
  for (let i = buckets.length - 1; i >= 0; i -= 1) {
    if (t >= buckets[i].start.getTime()) {
      // Also inside the range end (now); anything before bucket 0 falls out.
      return i;
    }
  }
  return -1;
};

/**
 * Activity history — mockup data-source-hub section E. The unified job timeline
 * from GET /api/v1/data-sources/{id}/activity rendered as a stacked bar chart with
 * clickable legend chips (toggle a series off) and a 13w / 6m / 12m range toggle.
 * Config-change + connection-test events aren't persisted yet (backend deferral),
 * so only job families render.
 */
function ActivityHistory({ dataSourceId }) {
  const [events, setEvents] = useState(null);
  const [disabled, setDisabled] = useState({});
  const [rangeKey, setRangeKey] = useState('13w');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await getDataSourceActivity(dataSourceId, { size: 500 });
        if (!cancelled) setEvents(result.content);
      } catch (e) {
        console.log('Failed to load data-source activity', e);
        if (!cancelled) setEvents([]);
      }
    };
    if (dataSourceId != null) load();
    return () => {
      cancelled = true;
    };
  }, [dataSourceId]);

  const range = RANGES.find((r) => r.key === rangeKey) || RANGES[0];

  const { buckets, maxTotal } = useMemo(() => {
    const bucketList = buildBuckets(range).map((b) => ({ ...b, counts: {} }));
    (events || []).forEach((event) => {
      const key = seriesKeyOf(event);
      if (!key || disabled[key]) return;
      const idx = bucketIndexFor(event.startedAt || event.finishedAt, bucketList, range);
      if (idx < 0) return;
      bucketList[idx].counts[key] = (bucketList[idx].counts[key] || 0) + 1;
    });
    const max = Math.max(1, ...bucketList.map((b) => Object.values(b.counts).reduce((a, n) => a + n, 0)));
    return { buckets: bucketList, maxTotal: max };
  }, [events, disabled, rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (events == null) return null;

  const tickEvery = range.bucket === 'week' ? 2 : 1;

  return (
    <div className='mb-3'>
      <h5 className='text-left sg-eyebrow'>Activity history</h5>
      <Card>
        <Card.Body>
          <div className='d-flex align-items-center mb-3' style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className='sg-small' style={{ color: 'var(--ink-500)' }}>
              Every discovery, fingerprint, masking and encrypt job against this data source. Toggle a series to filter.
            </span>
            <div className='ml-auto d-flex' style={{ gap: 4 }}>
              {RANGES.map((r) => (
                <Button
                  key={r.key}
                  size='sm'
                  variant={rangeKey === r.key ? 'dark' : 'outline-secondary'}
                  onClick={() => setRangeKey(r.key)}
                >
                  {r.label}
                </Button>
              ))}
            </div>
          </div>
          <div className='d-flex mb-2' style={{ gap: 10, flexWrap: 'wrap' }}>
            {SERIES.map((s) => (
              <button
                key={s.key}
                type='button'
                onClick={() => setDisabled((prev) => ({ ...prev, [s.key]: !prev[s.key] }))}
                className='sg-small'
                style={{
                  border: 'none',
                  background: 'transparent',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  opacity: disabled[s.key] ? 0.4 : 1,
                  textDecoration: disabled[s.key] ? 'line-through' : 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
                data-testid={`activity-legend-${s.key}`}
              >
                <span style={{ width: 9, height: 9, background: s.color, borderRadius: 2, flex: 'none' }} />
                {s.label}
              </button>
            ))}
          </div>
          {events.length === 0 ? (
            <div className='sg-small text-center' style={{ color: 'var(--ink-400)', padding: 24 }}>
              No activity recorded for this data source yet.
            </div>
          ) : (
            <div>
              <div
                className='d-flex align-items-end'
                style={{ height: 160, gap: 4, borderBottom: `1px solid ${INK[150]}` }}
              >
                {buckets.map((bucket, i) => {
                  const total = Object.values(bucket.counts).reduce((a, n) => a + n, 0);
                  const title = `${bucket.label}: ${SERIES.filter((s) => bucket.counts[s.key])
                    .map((s) => `${bucket.counts[s.key]} ${s.label.toLowerCase()}`)
                    .join(', ') || 'no activity'}`;
                  return (
                    <div
                      key={i}
                      title={title}
                      className='d-flex flex-column-reverse'
                      style={{ flex: 1, height: '100%', justifyContent: 'flex-start' }}
                    >
                      {SERIES.map((s) =>
                        bucket.counts[s.key] ? (
                          <div
                            key={s.key}
                            style={{
                              background: s.color,
                              height: `${(bucket.counts[s.key] / maxTotal) * 100}%`,
                              minHeight: 3,
                              borderRadius: 1,
                            }}
                          />
                        ) : null
                      )}
                      {total === 0 && <div style={{ height: 0 }} />}
                    </div>
                  );
                })}
              </div>
              <div className='d-flex' style={{ gap: 4 }}>
                {buckets.map((bucket, i) => (
                  <div
                    key={i}
                    className='text-center'
                    style={{ flex: 1, fontFamily: FONT_MONO, fontSize: 9.5, color: 'var(--ink-400)' }}
                  >
                    {i % tickEvery === 0 ? bucket.label : ''}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default ActivityHistory;
